import React, { useMemo, useState } from 'react';
import { Check, ListFilter } from 'lucide-react';
import { Table, TableColumn, TableSort } from '../ui';
import { useIsMobile } from '../../hooks/useIsMobile';
import {
    DashboardRFQ, StageFilter, OwnerView, ACTIVE_PIPELINE_STAGES,
} from '../../hooks/useDashboard';
import type { RowResearch } from '../../hooks/useResearchProgress';
import { MobileRfqList } from './MobileRfqList';
import { Progress, StageTag, timeAgo } from './RFQRowParts';

/**
 * Active RFQs table per `Dashboard v2.dc.html` — the v2 `Table` system
 * (never hand-rolled rows): project + mono id/owner, stage tag, honest n/9
 * progress on the pipeline ramp, parts count, updated-ago. Filter
 * dimensions (owner, 28 Jul):
 * ownership My projects / Everyone · stage / needs-review. Always active
 * RFQs only — no status toggle by design: awarded RFQs are done and don't
 * render here (matching the summary's activeTotal). Row click continues in
 * the wizard.
 */
interface RFQTableProps {
    rows: DashboardRFQ[];
    ownerView: OwnerView;
    onOwnerViewChange: (view: OwnerView) => void;
    stageFilter: StageFilter;
    onStageFilterChange: (filter: StageFilter) => void;
    counts: { all: number; needsReview: number };
    loading?: boolean;
    /** Live research for rows whose supplier research is running. */
    research?: Record<string, RowResearch>;
    onRowClick: (id: string) => void;
}

const LOCKED_STAGES = ['Financing', 'Delivery', 'Installation & Support', 'Contracting'];

const NO_RESEARCH: Record<string, RowResearch> = {};

const buildColumns = (research: Record<string, RowResearch>): TableColumn<DashboardRFQ>[] => [
    {
        key: 'project',
        header: 'Project',
        sortable: true,
        render: (row) => (
            <span className="min-w-0">
                <span className="flex min-w-0 items-center gap-s2">
                    <span className={`overflow-hidden text-ellipsis whitespace-nowrap text-[13px] ${row.name ? 'font-semibold text-ink' : 'font-medium text-ink-muted'}`}>
                        {row.name ?? 'Untitled project'}
                    </span>
                    {row.pendingOnEndUser && (
                        <span
                            className="shrink-0 whitespace-nowrap rounded-[6px] border border-warn-border bg-warn-bg px-[6px] py-[1px] text-[10.5px] font-semibold text-warn-ink"
                            title={row.endUserLabel ? `Technical details pending on ${row.endUserLabel}` : 'Technical details pending on the end user'}
                        >
                            {row.iAmEndUser ? 'Yours to fill' : 'With end user'}
                        </span>
                    )}
                </span>
                <span className="font-mono text-[11px] text-ink-muted">
                    {row.id}{row.owner ? ` · ${row.owner}` : ''}
                </span>
            </span>
        ),
    },
    { key: 'stage', header: 'Stage', width: '120px', sortable: true, render: (row) => <StageTag row={row} research={research[row.id]} /> },
    { key: 'progress', header: 'Progress', width: 'minmax(110px,150px)', render: (row) => <Progress row={row} research={research[row.id]} /> },
    { key: 'parts', header: 'Parts', width: '50px', mono: true, sortable: true, render: (row) => <>{row.partsCount}</> },
    { key: 'updated', header: 'Updated', width: '80px', sortable: true, render: (row) => <span className="text-[12px] text-ink-muted">{timeAgo(row.updatedAtIso)}</span> },
];

const sortValue = (row: DashboardRFQ, key: string): string | number => {
    switch (key) {
        case 'project': return (row.name ?? row.id).toLowerCase();
        case 'stage': return row.stageStep;
        case 'parts': return row.partsCount;
        case 'updated': return new Date(row.updatedAtIso).getTime();
        default: return 0;
    }
};

const Chip: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({ active, onClick, children }) => (
    <button
        type="button"
        onClick={onClick}
        className={`cursor-pointer rounded-[6px] px-[9px] py-[3px] text-[12px] transition-colors ${
            active ? 'bg-action-bg font-semibold text-action' : 'font-medium text-neutral-ink hover:bg-neutral-bg'
        }`}
    >
        {children}
    </button>
);

/** Bordered segmented control — reads as a filter, not clickable text. */
function Segmented<T extends string>({ value, options, onChange, label }: {
    value: T;
    options: { value: T; label: string }[];
    onChange: (value: T) => void;
    label: string;
}): React.ReactElement {
    return (
        <span role="group" aria-label={label} className="inline-flex overflow-hidden rounded-control border border-line">
            {options.map((opt, i) => (
                <button
                    key={opt.value}
                    type="button"
                    onClick={() => onChange(opt.value)}
                    aria-pressed={value === opt.value}
                    className={`cursor-pointer whitespace-nowrap px-[10px] py-[4px] text-[12px] transition-colors ${i > 0 ? 'border-l border-line' : ''} ${
                        value === opt.value ? 'bg-action-bg font-semibold text-action' : 'bg-surface font-medium text-neutral-ink hover:bg-neutral-bg'
                    }`}
                >
                    {opt.label}
                </button>
            ))}
        </span>
    );
}

const StageMenu: React.FC<{ stageFilter: StageFilter; onPick: (s: StageFilter) => void }> = ({ stageFilter, onPick }) => (
    <div className="absolute right-0 top-full z-20 mt-s2 flex w-60 flex-col rounded-card border border-line bg-surface py-s2 shadow-floating">
        {(['all', ...ACTIVE_PIPELINE_STAGES] as StageFilter[]).map((stage) => {
            const selected = stageFilter === stage;
            return (
                <button
                    key={stage}
                    type="button"
                    onClick={() => onPick(stage)}
                    className={`flex cursor-pointer items-center justify-between px-s5 py-[8px] text-left text-body-sm transition-colors ${
                        selected ? 'bg-action-bg font-semibold text-action' : 'text-ink-body hover:bg-surface-hover'
                    }`}
                >
                    {stage === 'all' ? 'All stages' : stage}
                    {selected && <Check className="h-[13px] w-[13px]" />}
                </button>
            );
        })}
        {LOCKED_STAGES.map((stage) => (
            <span key={stage} className="cursor-not-allowed px-s5 py-[8px] text-body-sm text-ink-muted/60">
                {stage} (soon)
            </span>
        ))}
    </div>
);

const emptyMessage = (stageFilter: StageFilter, ownerView: OwnerView): string => {
    if (stageFilter === 'needs-review') return 'Nothing is waiting on your review right now.';
    if (stageFilter !== 'all') return 'No RFQs in this stage.';
    if (ownerView === 'mine') return 'You have no active RFQs — New RFQ starts one.';
    return 'No active RFQs yet — start one with New RFQ.';
};

const RFQTable: React.FC<RFQTableProps> = ({
    rows, ownerView, onOwnerViewChange,
    stageFilter, onStageFilterChange, counts, loading = false, research = NO_RESEARCH, onRowClick,
}) => {
    const columns = useMemo(() => buildColumns(research), [research]);
    const [sort, setSort] = useState<TableSort | undefined>();
    const [stageOpen, setStageOpen] = useState(false);
    const isMobile = useIsMobile();

    const sorted = useMemo(() => {
        if (!sort) return rows;
        const dir = sort.direction === 'asc' ? 1 : -1;
        return rows.slice().sort((a, b) => {
            const va = sortValue(a, sort.key);
            const vb = sortValue(b, sort.key);
            return va < vb ? -dir : va > vb ? dir : 0;
        });
    }, [rows, sort]);

    const stageSelected = stageFilter !== 'all' && stageFilter !== 'needs-review';

    return (
        <section className="overflow-hidden rounded-card border border-line bg-surface">
            <div className="flex flex-wrap items-center gap-x-[14px] gap-y-s3 border-b border-line-divider-soft px-s5 py-[12px] md:px-s6">
                <span className="text-[13.5px] font-semibold text-ink">Active RFQs</span>
                <div className="flex flex-wrap gap-s1">
                    <Chip active={stageFilter === 'all'} onClick={() => onStageFilterChange('all')}>All · {counts.all}</Chip>
                    <Chip active={stageFilter === 'needs-review'} onClick={() => onStageFilterChange('needs-review')}>Needs review · {counts.needsReview}</Chip>
                </div>
                <div className="ml-auto flex w-full flex-wrap items-center gap-s3 md:w-auto">
                    <span className="hidden text-meta text-ink-muted md:inline">Filter by</span>
                    <Segmented<OwnerView>
                        label="Owner"
                        value={ownerView}
                        onChange={onOwnerViewChange}
                        options={[{ value: 'mine', label: 'My projects' }, { value: 'everyone', label: 'Everyone' }]}
                    />
                    <div className="relative">
                        <button
                            type="button"
                            onClick={() => setStageOpen((open) => !open)}
                            aria-expanded={stageOpen}
                            className={`flex cursor-pointer items-center gap-[6px] rounded-control border px-[10px] py-[4px] text-[12px] transition-colors ${
                                stageSelected
                                    ? 'border-action-border bg-action-bg font-semibold text-action'
                                    : 'border-line bg-surface font-medium text-neutral-ink hover:bg-neutral-bg'
                            }`}
                        >
                            <ListFilter className="h-[13px] w-[13px]" />
                            {stageSelected ? stageFilter : 'Stage'}
                        </button>
                        {stageOpen && (
                            <StageMenu
                                stageFilter={stageFilter}
                                onPick={(stage) => { onStageFilterChange(stage); setStageOpen(false); }}
                            />
                        )}
                    </div>
                </div>
            </div>
            {isMobile ? (
                <MobileRfqList
                    rows={sorted}
                    research={research}
                    empty={emptyMessage(stageFilter, ownerView)}
                    loading={loading}
                    onRowClick={onRowClick}
                />
            ) : (
                <Table<DashboardRFQ>
                    aria-label="Active RFQs"
                    framed={false}
                    columns={columns}
                    rows={sorted}
                    rowKey={(row) => row.id}
                    sort={sort}
                    onSortChange={setSort}
                    onRowClick={(row) => onRowClick(row.id)}
                    loading={loading}
                    stickyHeader
                    maxHeight={480}
                    empty={emptyMessage(stageFilter, ownerView)}
                />
            )}
        </section>
    );
};

export default RFQTable;
